import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import _ from "lodash";
import { Grid } from "@material-ui/core";
import { InputText } from "@loanbossinc/component-library";
import UserConfirmationModal from "modals/UserConfirmationModal/UserConfirmationModal";

import { actionCreators as rentRollFieldActionCreators } from "./rentRollField.store";
import { actionCreators as reportGroupsActionCreators } from "../../../dashboard/dashboard.store";
import {actionCreators as noiDashboardActionCreators} from "../../../reportingComponents/noiCalculationDashboard/noiRegularCalculationDashboard/noiRegularCalculationDashboard.store";
import {actionCreators as dscrDashboardActionCreators} from "../../../reportingComponents/dscrCalculationDashboard/dscrRegularCalculationDashboard/dscrRegularCalculationDashboard.store";
import {actionCreators as dyDashboardActionCreators} from "../../../reportingComponents/debtYieldCalculationDashboard/debtYieldRegularCalculationDashboard/debtYieldRegularCalculationDashboard.store";

import {actionCreators as noiProjectedDashboardActionCreators} from "../../../reportingComponents/noiCalculationDashboard/noiProjectedCalculationDashboard/noiProjectedCalculationDashboard.store";
import {actionCreators as dscrProjectedDashboardActionCreators} from "../../../reportingComponents/dscrCalculationDashboard/dscrProjectedCalculationDashboard/dscrProjectedCalculationDashboard.store";
import {actionCreators as dyProjectedDashboardActionCreators} from "../../../reportingComponents/debtYieldCalculationDashboard/debtYieldProjectedCalculationDashboard/debtYieldProjectedCalculationDashboard.store";

import { FieldNameWrapper, PaddedWrapper, Row, SelectButtonWrapper, StyledSelectButton } from "./rentRollFieldStyles";
import SelectCalculationParameters, { CALCULATION_PERIOD_OPTIONS } from "./selectCalculationParameters";
import VacancyRateCalculation from "./vacancyRateCalculation";

const RentRollField = props => {
  const {
    deal,
    fieldName,
    selectedProperty,
    setRentRollFieldSelections,
    setRentRollSelectionsToDefault,
    isStep2Screen,
    getReportGroupsForDeal,
    reportConfigs,
    isEditing,
    isAdding,
    selectedField,
    maxMonthValidation,
    fieldsFilter,
    VacancyType,
    isAnythingWasUpdated,
    minimumVacancyRate,
    postRentRollField,
    putRentRollField,
    vacancyRateType,
    defaultInclude,
    showConfirmationModal,
    setDashboardSelections,
    locationModalIsOpenFrom,
    setDrawerSelections,
    currentSection,
    setFieldSelections,
    selectedRentRollField,
    calculationPeriod,
    marketRentType,
    shouldOverrideOccupied,
    shouldOverrideVacant,
    isOverrideVacantIncluded,
    vacantSuiteDefault,
    overrideOccupiedMonths,
    overrideVacantMonths,
    manualEntryRentPerSqFt,
    selectedChargeCodes,
    selectedFreeRentChargeCodes,
    isChargeCodesLoading,
    isChargeCodesAliasesLoading,
    isSavingField,
    setDscrCalculationDashboardSelections,
    setNoiCalculationsDashboardSelections,
    setDebtYieldCalculationDashboardSelections,
    shouldReplaceRentRollStepByMonths,
    replaceRentRollStepByMonths,
    shouldUseFreeRentChargeCodeByMonths,
    freeRentChargeCodeByMonths,
    isStraightLine
  } = props;
  const [properties, setProperties] = useState([]);

  const vacancyRateCalculationRef = useRef();

  useEffect(() => {
    if (deal.properties) {
      setProperties(_.sortBy(_.cloneDeep(deal.properties), ["name"]));
    }
    if (deal.properties.length === 1) {
      setRentRollFieldSelections({ selectedProperty: deal.properties[0] });
    }
  }, [deal]);

  useEffect(() => {
    if (!reportConfigs) {
      getReportGroupsForDeal({ dealId: deal.id });
    }
  }, [reportConfigs]);

  useEffect(() => {
    if (isAdding || isEditing) {
      setRentRollSelectionsToDefault();
    }
  }, [isAdding, isEditing]);

  useEffect(() => {
    if (isEditing && selectedField && properties) {
      const {
        actualVacancyType,
        vacancyRateType,
        name,
        calculationPeriod,
        overrideOccupiedMonths,
        shouldOverrideVacant,
        shouldOverrideOccupied,
        marketRentType,
        propertyId,
        overrideVacantMonths,
        minimumVacancyRate,
        value,
        manualEntryRentPerSqFt,
        shouldReplaceRentRollStepByMonths,
        replaceRentRollStepByMonths,
        shouldUseFreeRentChargeCodeByMonths,
        freeRentChargeCodeByMonths
      } = selectedField;

      setRentRollFieldSelections({
        selectedProperty: properties.find((prop) => prop.id === propertyId),
        actualVacancyType,
        fieldName: name,
        fieldValue: value,
        vacancyRateType,
        minimumVacancyRate,
        defaultInclude: selectedField.defaultInclude,
        calculationPeriod,
        shouldOverrideOccupied,
        shouldOverrideVacant,
        isOverrideVacantIncluded: selectedField.isOverrideVacantIncluded !== undefined ? selectedField.isOverrideVacantIncluded : true,
        overrideOccupiedMonths,
        overrideVacantMonths,
        shouldReplaceRentRollStepByMonths,
        replaceRentRollStepByMonths,
        shouldUseFreeRentChargeCodeByMonths,
        freeRentChargeCodeByMonths,
        marketRentType,
        manualEntryRentPerSqFt: manualEntryRentPerSqFt || null,
        maxMonthValidation: CALCULATION_PERIOD_OPTIONS.find((opt) => opt.value === calculationPeriod).monthAmount,
        isStraightLine: selectedField.isStraightLine
      });
    }
  }, [isEditing, selectedField, properties]);

  const onNameChange = event => {
    const {
      target: { value }
    } = event;

    setRentRollFieldSelections({ fieldName: value, isAnythingWasUpdated: true });
  };

  const onPropertySelected = property => {
    setRentRollFieldSelections({ selectedProperty: property });
  };

  const getPropertiesList = () => {
    const filtered = fieldsFilter && fieldsFilter !== "0" ? properties.filter(f => f.id === parseInt(fieldsFilter, 10)) : properties;

    return filtered.map((p, idx) => (
      <Row key={p.id} justifyContent="flex-start" borderTop={idx === 0}>
        <PaddedWrapper>
          <FieldNameWrapper>{p.name}</FieldNameWrapper>
        </PaddedWrapper>
        <SelectButtonWrapper>
          <StyledSelectButton id="select-metrics-btn" label="Select" onClick={() => onPropertySelected(p)} />
        </SelectButtonWrapper>
      </Row>
    ));
  };

  const handleNext = () => {
    if ((isEditing || selectedRentRollField) && !isAnythingWasUpdated) {
      setRentRollFieldSelections({
        isStep2Screen: true,
        selectedRentRollField: selectedField || selectedRentRollField
      });

      return;
    }

    let fieldProps;
    if (isEditing || selectedRentRollField) {
      const field = selectedField || selectedRentRollField;
      fieldProps = field ? { ...field } : {};
    } else {
      fieldProps = {
        dealId: deal.id,
        isFromRentRoll: true,
        isRentRollAnnualized: true,
        level: "property",
        propertyId: selectedProperty.id,
        type: "rentroll",
        value: 0
      };
    }

    fieldProps = {
      ...fieldProps,
      VacancyType,
      calculationPeriod,
      marketRentType,
      name: fieldName,
      shouldOverrideOccupied,
      shouldOverrideVacant,
      isOverrideVacantIncluded,
      vacancyRateType,
      vacantSuiteDefault,
      defaultInclude,
      shouldReplaceRentRollStepByMonths,
      shouldUseFreeRentChargeCodeByMonths,
      isStraightLine
    };

    if (minimumVacancyRate !== null) {
      fieldProps.minimumVacancyRate = minimumVacancyRate;
    }
    if (shouldOverrideOccupied) {
      fieldProps.overrideOccupiedMonths = overrideOccupiedMonths;
    } else {
      fieldProps.overrideOccupiedMonths = 0;
    }
    if (shouldOverrideVacant) {
      fieldProps.overrideVacantMonths = overrideVacantMonths;
    } else {
      fieldProps.overrideVacantMonths = 0;
    }
    if(shouldReplaceRentRollStepByMonths) {
      fieldProps.replaceRentRollStepByMonths = replaceRentRollStepByMonths;
    } else {
      fieldProps.replaceRentRollStepByMonths = 0;
    }
    if(shouldUseFreeRentChargeCodeByMonths) {
      fieldProps.freeRentChargeCodeByMonths = freeRentChargeCodeByMonths;
    } else {
      fieldProps.freeRentChargeCodeByMonths = 0;
    }
    if (minimumVacancyRate !== null) {
      fieldProps.minimumVacancyRate = minimumVacancyRate;
    }
    if (manualEntryRentPerSqFt !== null) {
      fieldProps.manualEntryRentPerSqFt = manualEntryRentPerSqFt;
    }
    if (selectedChargeCodes && selectedChargeCodes.length > 0) {
      fieldProps.includedRentChargeCodes = selectedChargeCodes;
    }
    if (selectedFreeRentChargeCodes && selectedFreeRentChargeCodes.length > 0) {
      fieldProps.includedFreeRentChargeCodes = selectedFreeRentChargeCodes;
    }
    if (defaultInclude !== null) {
      fieldProps.defaultInclude = defaultInclude;
    }
    if (isStraightLine !== null) {
      fieldProps.isStraightLine = isStraightLine;
    }

    if (isEditing) {
      putRentRollField({ data: fieldProps });
    } else {
      postRentRollField({ data: fieldProps });
    }
  };

  const codesAndAliasesLoading = isChargeCodesAliasesLoading || isChargeCodesLoading;

  const isMonthInputValid = useCallback(
    (type) => {
      if (!calculationPeriod) {
        return true;
      }
      return !(
        (type === "occupied" &&
          shouldOverrideOccupied &&
          overrideOccupiedMonths !== null &&
          (overrideOccupiedMonths > maxMonthValidation || overrideOccupiedMonths < 0)) ||
        (type === "vacant" && shouldOverrideVacant && overrideVacantMonths !== null && (overrideVacantMonths > maxMonthValidation || overrideVacantMonths < 0)) ||
        (type === "replace" &&
          shouldReplaceRentRollStepByMonths &&
          replaceRentRollStepByMonths !== null &&
          (replaceRentRollStepByMonths > maxMonthValidation || replaceRentRollStepByMonths < 0)) ||
        (type === "freeRent" &&
          shouldUseFreeRentChargeCodeByMonths &&
          freeRentChargeCodeByMonths !== null &&
          (freeRentChargeCodeByMonths > maxMonthValidation || freeRentChargeCodeByMonths < 0))
      );
    },
    [calculationPeriod,
      shouldOverrideOccupied,
      overrideOccupiedMonths,
      shouldOverrideVacant,
      overrideVacantMonths,
      shouldReplaceRentRollStepByMonths,
      replaceRentRollStepByMonths,
      shouldUseFreeRentChargeCodeByMonths,
      freeRentChargeCodeByMonths]
  );

  const isFreeRentSectionInvalid = () => {
    const isHasMonthsInvalid = freeRentChargeCodeByMonths && !isMonthInputValid("freeRent");
    const isFreeREntChargeCodeDoNotHaveValue = !selectedFreeRentChargeCodes || (selectedFreeRentChargeCodes && selectedFreeRentChargeCodes.length === 0);
    return shouldUseFreeRentChargeCodeByMonths && (isHasMonthsInvalid || isFreeREntChargeCodeDoNotHaveValue);
  };

  const isNextDisabled = useMemo(() => {
    return (
      !fieldName ||
      !calculationPeriod ||
      !marketRentType ||
      !vacantSuiteDefault ||
      (marketRentType === "manualEntry" && !manualEntryRentPerSqFt) ||
      (shouldOverrideOccupied && !overrideOccupiedMonths) ||
      (shouldOverrideVacant && !overrideVacantMonths) ||
      (shouldUseFreeRentChargeCodeByMonths && !freeRentChargeCodeByMonths) ||
      (shouldOverrideOccupied && overrideOccupiedMonths && !isMonthInputValid("occupied")) ||
      (shouldOverrideVacant && overrideVacantMonths && !isMonthInputValid("vacant")) ||
      (shouldReplaceRentRollStepByMonths && replaceRentRollStepByMonths && !isMonthInputValid("replace")) ||
      (isFreeRentSectionInvalid()) ||
      (selectedChargeCodes && selectedChargeCodes.length === 0) ||
      codesAndAliasesLoading
    );
  }, [
    fieldName,
    calculationPeriod,
    marketRentType,
    manualEntryRentPerSqFt,
    shouldOverrideOccupied,
    overrideOccupiedMonths,
    shouldOverrideVacant,
    overrideVacantMonths,
    selectedChargeCodes,
    selectedFreeRentChargeCodes,
    codesAndAliasesLoading,
    vacantSuiteDefault,
    shouldReplaceRentRollStepByMonths,
    replaceRentRollStepByMonths,
    shouldUseFreeRentChargeCodeByMonths,
    freeRentChargeCodeByMonths
  ]);

  const onDiscardChanges = () => {
    setRentRollFieldSelections({ showConfirmationModal: false, isAnythingWasUpdated: false });
    if (locationModalIsOpenFrom === "go_to_source_files") {
      setDrawerSelections({ currentSectionName: currentSection && currentSection.goToButtonValue });
    } else if (locationModalIsOpenFrom === "go_back_to_fields") {
      setFieldSelections({ isAdding: false, isEditing: false, selectedField: null });
    } else if (locationModalIsOpenFrom === "screen2") {
      setRentRollFieldSelections({ isStep2Screen: false, isAnythingWasUpdated: false });
    } else {
      setDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
      setDscrCalculationDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
      setDebtYieldCalculationDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
      setNoiCalculationsDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
    }
  };

  const onCloseConfirmationModal = () => {
    setRentRollFieldSelections({ showConfirmationModal: false });
  };

  const saveText = useMemo(() => {
    if (isSavingField) {
      return "Saving...";
    }
    return isStep2Screen ? "Save" : "Save and Continue";
  }, [isStep2Screen, isSavingField]);

  return (
    <>
      {isStep2Screen ? (
        <VacancyRateCalculation ref={vacancyRateCalculationRef} {...props} selectedProperty={selectedProperty} />
      ) : (
        <Grid id="rent-roll-field">
          <PaddedWrapper padding="1em">
            <InputText id="field-name-input" variant="filled" label="Field Name" name="field-name" fullWidth onChange={onNameChange} value={fieldName} />
          </PaddedWrapper>
          {selectedProperty ? (
            <SelectCalculationParameters
              {...props}
              selectedProperty={selectedProperty}
              isMonthInputValid={isMonthInputValid}
              handleNext={handleNext}
              isNextDisabled={isNextDisabled}
            />
          ) : (
            getPropertiesList()
          )}
        </Grid>
      )}
      <UserConfirmationModal
        open={showConfirmationModal}
        onClose={onCloseConfirmationModal}
        onCancelClick={onDiscardChanges}
        onConfirmClick={!isStep2Screen ? handleNext : vacancyRateCalculationRef.current && vacancyRateCalculationRef.current.onFinishClick}
        saveText={saveText}
        isDisabled={isNextDisabled}
      />
    </>
  );
};

const mapStateToProps = ({ lenderReportRentRollField, lenderReportList: { reportConfigs, selectedReportGroup }, authentication: { isSystemAdmin } }) => ({
  ...lenderReportRentRollField,
  isSystemAdmin,
  reportConfigs,
  selectedReportGroup
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...rentRollFieldActionCreators,
      ...reportGroupsActionCreators,
      ...noiDashboardActionCreators,
      ...dscrDashboardActionCreators,
      ...dyDashboardActionCreators,
      ...noiProjectedDashboardActionCreators,
      ...dscrProjectedDashboardActionCreators,
      ...dyProjectedDashboardActionCreators
    },
    dispatch
  );

export default compose(connect(mapStateToProps, mapDispatchToProps))(RentRollField);
